import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Filter, Download, Search } from 'lucide-react';
import type { Attendance } from '@/types';
import { api } from '@/data/mockData';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar as CalendarIcon, Clock, UserPlus, Check, X } from "lucide-react";

const Attendance = () => {
  const [attendanceData, setAttendanceData] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const { toast } = useToast();
  const [openMarkAttendanceDialog, setOpenMarkAttendanceDialog] = useState(false);
  const [openBulkUploadDialog, setOpenBulkUploadDialog] = useState(false);

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const data = await api.getAttendance(selectedEmployee, selectedMonth);
        setAttendanceData(data);
      } catch (error) {
        console.error('Error fetching attendance:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch attendance data',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [selectedEmployee, selectedMonth, toast]);

  const employees = [
    { id: "EMP001", name: "John Smith" },
    { id: "EMP002", name: "Sarah Johnson" },
    { id: "EMP003", name: "Michael Brown" },
    { id: "EMP004", name: "Emily Davis" },
    { id: "EMP005", name: "Jessica Martinez" }
  ];

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? employee.name : employeeId;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "MMMM d, yyyy");
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return "-";
    
    // Assuming timeString is in format "HH:MM:SS"
    const [hours, minutes] = timeString.split(":");
    const hoursNum = parseInt(hours, 10);
    const period = hoursNum >= 12 ? "PM" : "AM";
    const hour12 = hoursNum % 12 || 12;
    
    return `${hour12}:${minutes} ${period}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "present":
        return <Badge className="bg-green-100 text-green-800 border-green-200">Present</Badge>;
      case "absent":
        return <Badge className="bg-red-100 text-red-800 border-red-200">Absent</Badge>;
      case "half_day":
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Half Day</Badge>;
      case "leave":
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Leave</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Attendance Management</h1>
            <p className="text-gray-500">Track and manage employee attendance records</p>
          </div>
          <div className="space-x-2">
            <Dialog open={openMarkAttendanceDialog} onOpenChange={setOpenMarkAttendanceDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Check className="mr-2 h-4 w-4" /> Mark Attendance
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Mark Attendance</DialogTitle>
                  <DialogDescription>
                    Record attendance for an employee
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="employee">Employee</Label>
                    <select id="employee" className="w-full rounded-md border border-gray-300 p-2">
                      <option value="">Select Employee</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !selectedMonth && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {selectedMonth ? format(new Date(selectedMonth), "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={selectedMonth ? new Date(selectedMonth) : undefined}
                          onSelect={(date) => setSelectedMonth(date?.toISOString().split('T')[0] || '')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <select className="w-full rounded-md border border-gray-300 p-2">
                      <option value="present">Present</option>
                      <option value="absent">Absent</option>
                      <option value="half_day">Half Day</option>
                      <option value="leave">Leave</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="checkIn">Check In</Label>
                      <Input id="checkIn" type="time" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="checkOut">Check Out</Label>
                      <Input id="checkOut" type="time" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Input id="notes" />
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setOpenMarkAttendanceDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="bg-payroll-primary hover:bg-blue-700" 
                    onClick={() => setOpenMarkAttendanceDialog(false)}
                  >
                    Save
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Dialog open={openBulkUploadDialog} onOpenChange={setOpenBulkUploadDialog}>
              <DialogTrigger asChild>
                <Button className="bg-payroll-primary hover:bg-blue-700">
                  <UserPlus className="mr-2 h-4 w-4" /> Bulk Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Bulk Upload Attendance</DialogTitle>
                  <DialogDescription>
                    Upload a CSV file with attendance records
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="space-y-2">
                    <Label>CSV File</Label>
                    <Input type="file" accept=".csv" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Template Format</p>
                    <p className="text-xs text-gray-500">
                      The CSV file should contain the following columns:<br />
                      Employee ID, Date, Check In, Check Out, Status
                    </p>
                  </div>
                  <Button variant="outline" className="w-full">
                    Download Template
                  </Button>
                </div>
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setOpenBulkUploadDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="bg-payroll-primary hover:bg-blue-700" 
                    onClick={() => setOpenBulkUploadDialog(false)}
                  >
                    Upload
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-1/3">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search employees..."
              className="pl-9"
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            />
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {selectedMonth ? format(new Date(selectedMonth), "MMMM yyyy") : "Filter by Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={selectedMonth ? new Date(selectedMonth) : undefined}
                  onSelect={(date) => setSelectedMonth(date?.toISOString().split('T')[0] || '')}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button variant="outline" className="flex items-center gap-2">
              <Filter className="h-4 w-4" /> More Filters
            </Button>
            <Button variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="daily">
          <TabsList>
            <TabsTrigger value="daily">Daily View</TabsTrigger>
            <TabsTrigger value="weekly">Weekly View</TabsTrigger>
            <TabsTrigger value="monthly">Monthly View</TabsTrigger>
          </TabsList>
          
          <TabsContent value="daily" className="mt-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Daily Attendance</CardTitle>
                <CardDescription>
                  View and manage daily attendance records
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm font-medium text-gray-500">
                        <th className="pb-3 pl-4">Employee</th>
                        <th className="pb-3">Date</th>
                        <th className="pb-3">Check In</th>
                        <th className="pb-3">Check Out</th>
                        <th className="pb-3">Work Hours</th>
                        <th className="pb-3">Overtime</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceData.map((attendance) => (
                        <tr key={attendance.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 pl-4">{getEmployeeName(attendance.employeeId)}</td>
                          <td className="py-4">{formatDate(attendance.date)}</td>
                          <td className="py-4">{formatTime(attendance.checkIn)}</td>
                          <td className="py-4">{formatTime(attendance.checkOut)}</td>
                          <td className="py-4">{attendance.workHours.toFixed(2)}</td>
                          <td className="py-4">{attendance.overtime > 0 ? `${attendance.overtime.toFixed(2)}` : '-'}</td>
                          <td className="py-4">{getStatusBadge(attendance.status)}</td>
                          <td className="py-4 pr-4 space-x-2">
                            <Button variant="outline" size="sm">Edit</Button>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700">
                              <X className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="weekly" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Weekly Attendance Summary</CardTitle>
                <CardDescription>
                  View weekly attendance records and totals
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm font-medium text-gray-500">
                        <th className="pb-3 pl-4">Employee</th>
                        <th className="pb-3">Week</th>
                        <th className="pb-3">Present Days</th>
                        <th className="pb-3">Absent Days</th>
                        <th className="pb-3">Leave Days</th>
                        <th className="pb-3">Total Hours</th>
                        <th className="pb-3">Total Overtime</th>
                        <th className="pb-3 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 pl-4">John Smith</td>
                        <td className="py-4">Apr 1 - Apr 7, 2025</td>
                        <td className="py-4">4</td>
                        <td className="py-4">1</td>
                        <td className="py-4">0</td>
                        <td className="py-4">33.16</td>
                        <td className="py-4">0.33</td>
                        <td className="py-4 pr-4">
                          <Button variant="outline" size="sm">View Details</Button>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 pl-4">Sarah Johnson</td>
                        <td className="py-4">Apr 1 - Apr 7, 2025</td>
                        <td className="py-4">4</td>
                        <td className="py-4">0</td>
                        <td className="py-4">1</td>
                        <td className="py-4">31.66</td>
                        <td className="py-4">0</td>
                        <td className="py-4 pr-4">
                          <Button variant="outline" size="sm">View Details</Button>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 pl-4">Michael Brown</td>
                        <td className="py-4">Apr 1 - Apr 7, 2025</td>
                        <td className="py-4">3</td>
                        <td className="py-4">1</td>
                        <td className="py-4">1</td>
                        <td className="py-4">28.5</td>
                        <td className="py-4">1.5</td>
                        <td className="py-4 pr-4">
                          <Button variant="outline" size="sm">View Details</Button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="monthly" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Attendance Summary</CardTitle>
                <CardDescription>         
                  View monthly attendance records and totals
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm font-medium text-gray-500">
                        <th className="pb-3 pl-4">Employee</th>
                        <th className="pb-3">Month</th>
                        <th className="pb-3">Present Days</th>
                        <th className="pb-3">Absent Days</th>
                        <th className="pb-3">Leave Days</th>
                        <th className="pb-3">Working Days</th>
                        <th className="pb-3">Attendance %</th>
                        <th className="pb-3 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 pl-4">John Smith</td>
                        <td className="py-4">April 2025</td>
                        <td className="py-4">20</td>
                        <td className="py-4">2</td>
                        <td className="py-4">0</td>
                        <td className="py-4">22</td>
                        <td className="py-4">90.91%</td>
                        <td className="py-4 pr-4">
                          <Button variant="outline" size="sm">View Details</Button>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 pl-4">Sarah Johnson</td>
                        <td className="py-4">April 2025</td>
                        <td className="py-4">18</td>
                        <td className="py-4">0</td>
                        <td className="py-4">4</td>
                        <td className="py-4">22</td>
                        <td className="py-4">81.82%</td>
                        <td className="py-4 pr-4">
                          <Button variant="outline" size="sm">View Details</Button>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 pl-4">Michael Brown</td>
                        <td className="py-4">April 2025</td>
                        <td className="py-4">17</td>
                        <td className="py-4">3</td>
                        <td className="py-4">2</td>
                        <td className="py-4">22</td>
                        <td className="py-4">77.27%</td>
                        <td className="py-4 pr-4">
                          <Button variant="outline" size="sm">View Details</Button>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 pl-4">Emily Davis</td>
                        <td className="py-4">April 2025</td>
                        <td className="py-4">21</td>
                        <td className="py-4">1</td>
                        <td className="py-4">0</td>
                        <td className="py-4">22</td>
                        <td className="py-4">95.45%</td>
                        <td className="py-4 pr-4">
                          <Button variant="outline" size="sm">View Details</Button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default Attendance;
